import type { AdminStorefrontDetail } from '@/lib/api/storefrontAdminHooks';

import { CopyButton, Field, Panel, Pill } from './ui';

/** The setup form's choices (system-web lib/rxstore/partner-setup.js;
 *  backend SOCIAL_PLATFORMS / AUDIENCE_SIZES). An unknown value shows as
 *  written. */
const PLATFORM_LABEL: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  facebook: 'Facebook',
  twitch: 'Twitch',
  podcast: 'Podcast',
  newsletter: 'Newsletter',
  other: 'Other',
};
const AUDIENCE_SIZE_LABEL: Record<string, string> = {
  under_1k: 'Under 1,000',
  '1k_10k': '1,000 to 10,000',
  '10k_50k': '10,000 to 50,000',
  '50k_250k': '50,000 to 250,000',
  '250k_1m': '250,000 to 1 million',
  over_1m: 'Over 1 million',
};

/**
 * Who runs the store, for the go / no-go (contract D4): the owner's full
 * name and email, the creator name the store's copy signs with (the only
 * public one), and the social handle and audience the owner gave at setup.
 * Owner-entered and unverified; shown to admins only.
 */
export function OwnerIdentityPanel({ store }: { store: AdminStorefrontDetail }) {
  const owner = store.members.find((m) => m.access === 'owner') ?? null;
  const fullName = store.owner_full_name ?? owner?.full_name ?? null;
  const email = store.owner_email ?? owner?.email ?? null;
  const profile = store.owner_profile ?? {};
  const handle = (profile.social_handle ?? '').trim();
  const platformRaw = (profile.social_platform ?? '').trim();
  const platform = PLATFORM_LABEL[platformRaw.toLowerCase()] ?? platformRaw;
  const audienceSizeRaw = (profile.audience_size ?? '').trim();
  const audienceSize = AUDIENCE_SIZE_LABEL[audienceSizeRaw.toLowerCase()] ?? audienceSizeRaw;
  const audience = (profile.audience_description ?? '').trim();
  const missing = <span className="text-ink-muted">Not given</span>;
  return (
    <Panel
      title="Who runs this store"
      aside={<span className="type-label-xs text-ink-muted normal-case">Owner-entered · admins only</span>}
    >
      <dl className="grid grid-cols-2 gap-14">
        <Field label="Owner" className="col-span-2">
          {fullName || email ? (
            <span className="flex flex-col gap-2">
              <span>{fullName ?? <span className="text-ink-muted">Name not given</span>}</span>
              {email && <span className="text-ink-secondary break-all">{email}</span>}
            </span>
          ) : (
            <span className="text-warning">No owner</span>
          )}
        </Field>
        <Field label="Creator name (public)" className="col-span-2">
          {store.creator_name ? (
            <span className="flex flex-wrap items-center gap-6">
              {store.creator_name}
              {!store.creator_name_custom && (
                <Pill className="normal-case">From the owner&apos;s first name</Pill>
              )}
            </span>
          ) : (
            <span className="text-ink-muted">None (the copy uses the store name)</span>
          )}
        </Field>
        <Field label="Social handle" className="col-span-2">
          {handle ? (
            <span className="flex flex-wrap items-center gap-8">
              <span className="font-mono text-12 break-all">{handle}</span>
              {platform && <Pill className="normal-case">{platform}</Pill>}
              <CopyButton value={handle} label="Copy" className="h-24 px-8" />
            </span>
          ) : (
            missing
          )}
        </Field>
        <Field label="Audience size" className="col-span-2">
          {audienceSize || missing}
        </Field>
        <Field label="Audience" className="col-span-2">
          {audience ? <span className="whitespace-pre-wrap">{audience}</span> : missing}
        </Field>
      </dl>
    </Panel>
  );
}
